import { computed, nextTick, provide, ref, watch, type ComputedRef } from 'vue';
import { parseExtraCellNodeId, TABLE_NEW_ROWS_LIMIT, type CompiledNode } from '@record-fill';
import type { ExtraGridCell } from './fill-table-styles';
import { SELECTED_NEW_ROW_KEY, type FillContext } from './fill-context';

interface PendingRemoval {
  rowIndex: number;
  fromCount: number;
}

function extraRowCount(node: CompiledNode): number {
  return node.kind === 'table' ? node.extraRows.length : 0;
}

export function useMultipleTableRows(
  node: () => CompiledNode,
  context: FillContext | null,
  extraGridCells: ComputedRef<ExtraGridCell[]>,
  emitRemove: (tableId: string, rowIndex: number) => void,
) {
  // A field, a text or an image never turns into a table under the same node id; a table may gain
  // or lose "Tabela múltipla", so tables keep the reactive path.
  if (node().kind !== 'table') {
    return {
      hasRemovableRow: computed(() => false),
      atRowLimit: computed(() => false),
      firstExtraColumn: computed<number | undefined>(() => undefined),
      extraCellKey: (extra: ExtraGridCell) => extra.cell.id,
      removeRow: () => undefined,
    };
  }

  const newRows = computed(() => {
    const current = node();
    return current.kind === 'table' ? current.extraRows.filter((row) => !row.historical) : [];
  });
  const hasRemovableRow = computed(() => newRows.value.length > 0);
  const atRowLimit = computed(() => newRows.value.length >= TABLE_NEW_ROWS_LIMIT);

  // The row comes from the highlighted field, which the page sets on focus and on click: the
  // browser focus would not do, since a click focuses no button, checkbox or radio in Safari.
  const selectedNewRow = computed<number | null>(() => {
    if (context?.readOnly.value === true) return null;
    const address = parseExtraCellNodeId(context?.highlightedFieldId.value ?? '');
    if (address?.tableId !== node().id) return null;
    const selected = newRows.value.find((row) => row.rowIndex === address.rowIndex);
    const cell = selected?.cells.find((item) => item.columnIndex === address.columnIndex)?.node;
    return selected && cell?.state.visible ? selected.rowIndex : null;
  });
  provide(SELECTED_NEW_ROW_KEY, selectedNewRow);

  const firstExtraColumn = computed(() => {
    const cells = extraGridCells.value;
    let first: number | undefined;
    for (const extra of cells) {
      if (extra.rowIndex !== cells[0].rowIndex) break;
      first = first === undefined ? extra.columnIndex : Math.min(first, extra.columnIndex);
    }
    return first;
  });

  // Rows are positional in the answers, so the sheet keeps its own key per row: a cell follows its
  // row and keeps the local state (typed text) of that row when a row above leaves. Only the
  // removed row unmounts; remounting every cell took over a second with 200 rows. The keys follow
  // the row count only: one more row is an append, one less is the removal the sheet asked for,
  // and any other count change gets fresh keys.
  let nextKey = 0;
  const freshKeys = (count: number) => Array.from({ length: count }, () => nextKey++);
  const rowKeys = ref<number[]>(freshKeys(extraRowCount(node())));
  let pending: PendingRemoval | null = null;
  watch(
    () => extraRowCount(node()),
    (count, previous) => {
      const keys = [...rowKeys.value];
      if (count === previous + 1 && keys.length === previous) keys.push(nextKey++);
      else if (pending?.fromCount === previous && count === previous - 1) keys.splice(pending.rowIndex, 1);
      else keys.splice(0, keys.length, ...freshKeys(count));
      rowKeys.value = keys;
      pending = null;
    },
  );

  function extraCellKey(extra: ExtraGridCell): string {
    const rowKey = rowKeys.value[extra.rowIndex];
    return `${rowKey}-${extra.columnIndex}`;
  }

  function removeRow(): void {
    const rowIndex = selectedNewRow.value ?? newRows.value.at(-1)?.rowIndex;
    if (rowIndex === undefined) return;
    pending = { rowIndex, fromCount: extraRowCount(node()) };
    emitRemove(node().id, rowIndex);
    // A removal the page refuses changes no row count, so the watcher above never clears it; the
    // page answers synchronously and the watcher runs in this flush, before the tick resolves.
    void nextTick(() => {
      pending = null;
    });
  }

  return { hasRemovableRow, atRowLimit, firstExtraColumn, extraCellKey, removeRow };
}
